<!--
    see comment in _nav-layout.cshtml. Do not make any changes to the templates of
        pc-nav-layout.vue
        navHeader.vue
        navSidebar.vue
    without also making sure the corresponding changes are made to the necessary cshtml views.
-->
<template>
    <header class="bg-white">
        <div
            ref="wrapper"
            class="d-flex pc-nav-header justify-content-between">
            <div
                v-if="showSidebarMenu"
                class="d-flex align-items-center p-2">
                <header-icon
                    v-bind:aria-label="navSidebarLabel"
                    data-test-role="nav-sidebar-show"
                    icon-name="list"
                    v-on:click="showSidebarLeft" />
            </div>
            <div
                class="d-flex flex-shrink-0 align-items-center justify-content-center pc-nav-program-logo-wrapper static">
                <program-logo v-bind:aria-label="cleanPowerResearchLabel" />
            </div>
            <div
                v-if="!hideMenuItems"
                class="d-flex flex-grow-1 p-2">
                <div
                    v-if="showPrograms"
                    class="d-flex align-items-center p-2">
                    <programs-name-btn v-bind:collapse-level="programsCollapseLevel" />
                </div>
                <div class="d-flex flex-grow-1 justify-content-end align-items-center p-2">
                    <!-- TODO: enable section below when global search has a page -->
                    <template v-if="false">
                        <pc-search
                            v-if="showSearch"
                            placeholder="Search"
                            class="w-100 search-min-width" />
                        <header-icon
                            v-if="!showSearch"
                            tooltip="Search"
                            icon-name="search"
                            v-bind:class="{ active: showFullSearch }"
                            v-on:click="showFullSearch = !showFullSearch" />
                    </template>
                </div>
                <div
                    v-if="showInquiryInHeader"
                    class="d-flex align-items-center p-2">
                    <pc-button
                        data-test-role="navProjectInquiry"
                        outline
                        class="btn-outline-primary text-nowrap"
                        v-bind:data-pc-menu-name="menuTypeName.Inquiry"
                        v-on:click="$app.showQuestionDialog">
                        {{ inquiryMenuLabel }}
                    </pc-button>
                </div>
                <div
                    v-if="showMoreMenu"
                    class="d-flex align-items-center p-2">
                    <div class="dropdown">
                        <header-icon
                            data-test-role="navMoreDropdownIcon"
                            v-bind:tooltip="moreDropdownTooltip"
                            v-bind:aria-label="moreDropdownTooltip"
                            dropdown
                            icon-name="three-dots-vertical" />
                        <div
                            v-bind:class="dropdownMenuWrapperClass"
                            data-test-role="navMoreDropdown">
                            <template v-if="!showAccountInHeader">
                                <h6
                                    class="dropdown-header"
                                    v-bind:title="loggedInUserEmailTooltipText"
                                    data-bs-toggle="tooltip">
                                    {{ loggedInUserEmail }}
                                </h6>
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="!showPrograms">
                                <button
                                    data-test-role="navProgramsSidebarShow"
                                    class="dropdown-item"
                                    type="button"
                                    v-on:click="showProgramsSidebar">
                                    {{ programsButtonLabel }}
                                </button>
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="showInquiryInMenu">
                                <button
                                    data-test-role="navProjectInquiry"
                                    class="dropdown-item"
                                    type="button"
                                    v-bind:data-pc-menu-name="menuTypeName.Inquiry"
                                    v-on:click="$app.showQuestionDialog">
                                    {{ inquiryMenuLabel }}
                                </button>
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="showProgramSupportInMenu">
                                <button
                                    class="dropdown-item"
                                    type="button"
                                    v-bind:data-pc-menu-name="menuTypeName.ProgramSupport"
                                    v-on:click="showProgramSupportDialog">
                                    {{ programSupportLabel }}
                                </button>
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="showSupportInMenu">
                                <button
                                    class="dropdown-item"
                                    type="button"
                                    v-bind:data-pc-menu-name="menuTypeName.DesignSupport"
                                    v-on:click="$app.openWindow(supportCenterUrl, '_blank')">
                                    {{ supportCenterLinkLabel }}
                                </button>
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="showNotificationsInMenu">
                                <button
                                    class="dropdown-item"
                                    type="button"
                                    v-on:click="$app.setHref(notificationsUrl)">
                                    {{ notificationsLinkLabel }}
                                </button>
                            </template>
                            <template v-if="showNotificationsInMenu && !showAccountInHeader">
                                <div class="dropdown-divider" />
                            </template>
                            <template v-if="!showAccountInHeader">
                                <a
                                    class="dropdown-item"
                                    v-bind:data-pc-menu-name="menuTypeName.ManageAccount"
                                    v-bind:href="`${manageAccountMenuItem.Url}?ProgramId=${$app.ProgramId}`">
                                    {{ manageAccountMenuItem.Text }}
                                </a>
                                <button
                                    class="dropdown-item"
                                    type="button"
                                    data-test-role="navLogoutButton"
                                    v-on:click="$app.logout">
                                    {{ logOutLinkLabel }}
                                </button>
                                <div class="dropdown-divider" />
                                <a
                                    class="dropdown-item"
                                    v-bind:data-pc-menu-name="menuTypeName.PrivacyPolicy"
                                    v-bind:href="privacyPolicyMenuItem.Url"
                                    target="_blank">
                                    {{ privacyPolicyMenuItem.Text }}
                                </a>
                                <a
                                    class="dropdown-item"
                                    v-bind:data-pc-menu-name="menuTypeName.TermsOfUse"
                                    v-bind:href="termsOfUseMenuItem.Url"
                                    target="_blank">
                                    {{ termsOfUseMenuItem.Text }}
                                </a>
                            </template>
                        </div>
                    </div>
                </div>
                <div
                    v-if="showProgramSupportInHeader"
                    class="d-flex align-items-center p-2">
                    <header-icon
                        v-bind:tooltip="programSupportLabel"
                        icon-name="question-lg"
                        v-bind:data-pc-menu-name="menuTypeName.ProgramSupport"
                        v-bind:aria-label="programSupportLabel"
                        v-on:click="showProgramSupportDialog" />
                </div>
                <div
                    v-if="showSupportInHeader"
                    class="d-flex align-items-center p-2">
                    <header-icon
                        v-bind:tooltip="supportCenterLinkLabel"
                        tag="a"
                        v-bind:data-pc-menu-name="menuTypeName.DesignSupport"
                        v-bind:aria-label="supportCenterLinkLabel"
                        v-bind:href="supportCenterUrl"
                        target="_blank"
                        icon-name="question-lg" />
                </div>
                <div
                    v-if="showNotificationsInHeader"
                    class="d-flex align-items-center p-2">
                    <div class="dropdown">
                        <header-icon
                            data-test-role="navNotificationsDropdownIcon"
                            v-bind:tooltip="notificationsLinkLabel"
                            v-bind:aria-label="notificationsLinkLabel"
                            dropdown
                            icon-name="bell-fill" />
                        <div
                            data-test-role="navNotificationsDropdown"
                            class="p-0"
                            v-bind:class="dropdownMenuWrapperClass">
                            <div v-bind:style="notificationsDropdownStyle">
                                <notifications
                                    v-bind:data-pc-menu-name="menuTypeName.Notifications"
                                    v-bind:load-time="pageLoadTimeTicks"
                                    v-bind:opened="false" />
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="showAccountInHeader"
                    class="d-flex align-items-center p-2">
                    <div class="dropdown">
                        <header-icon
                            data-test-role="navAccountDropdownIcon"
                            v-bind:tooltip="accountDropdownTooltip"
                            v-bind:aria-label="accountDropdownTooltip"
                            dropdown
                            icon-name="person-fill" />
                        <div
                            v-bind:class="dropdownMenuWrapperClass"
                            data-test-role="navAccountDropdown">
                            <h6
                                class="dropdown-header"
                                v-bind:title="loggedInUserEmailTooltipText"
                                data-bs-toggle="tooltip">
                                {{ loggedInUserEmail }}
                            </h6>
                            <div class="dropdown-divider" />
                            <a
                                class="dropdown-item"
                                v-bind:data-pc-menu-name="menuTypeName.ManageAccount"
                                v-bind:href="`${manageAccountMenuItem.Url}?ProgramId=${$app.ProgramId}`">
                                {{ manageAccountMenuItem.Text }}
                            </a>
                            <button
                                class="dropdown-item"
                                type="button"
                                data-test-role="navLogoutButton"
                                v-on:click="$app.logout">
                                {{ logOutLinkLabel }}
                            </button>
                            <div class="dropdown-divider" />
                            <a
                                class="dropdown-item"
                                v-bind:data-pc-menu-name="menuTypeName.PrivacyPolicy"
                                v-bind:href="privacyPolicyMenuItem.Url"
                                target="_blank">
                                {{ privacyPolicyMenuItem.Text }}
                            </a>
                            <a
                                class="dropdown-item"
                                v-bind:data-pc-menu-name="menuTypeName.TermsOfUse"
                                v-bind:href="termsOfUseMenuItem.Url"
                                target="_blank">
                                {{ termsOfUseMenuItem.Text }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div class="d-flex align-items-center p-2">
                <div
                    v-if="hideMenuItems"
                    class="d-flex align-items-center p-2">
                    <div class="dropdown">
                        <header-icon
                            v-bind:tooltip="moreDropdownTooltip"
                            dropdown
                            icon-name="three-dots-vertical" />
                        <div v-bind:class="dropdownMenuWrapperClass">
                            <a
                                class="dropdown-item"
                                v-bind:data-pc-menu-name="menuTypeName.PrivacyPolicy"
                                v-bind:href="privacyPolicyMenuItem.Url"
                                target="_blank">
                                {{ privacyPolicyMenuItem.Text }}
                            </a>
                            <a
                                class="dropdown-item"
                                v-bind:data-pc-menu-name="menuTypeName.TermsOfUse"
                                v-bind:href="termsOfUseMenuItem.Url"
                                target="_blank">
                                {{ termsOfUseMenuItem.Text }}
                            </a>
                        </div>
                    </div>
                </div>
                <div class="d-flex align-items-center p-2">
                    <product-logo />
                </div>
            </div>
        </div>
        <div class="d-flex">
            <pc-transition transition="slide">
                <div
                    v-if="showFullSearch"
                    class="flex-grow-1 d-flex align-items-center p-2">
                    <pc-search
                        placeholder="Search"
                        class="w-100" />
                </div>
            </pc-transition>
        </div>
    </header>
</template>

<script>
    import { BP } from "Responsive/breakpoint";
    import notifications from "Menu/notificationsDropdown.vue";
    import programLogo from "./programLogo.vue";
    import productLogo from "./productLogo.vue";
    import programsNameBtn from "./programsNameBtn.vue";
    import navSidebar from "./navSidebar.vue";
    import navPrograms from "./navPrograms.vue";
    import headerIcon from "./header-icon.vue";
    import supportDialog from "Menu/support.vue";
    import { MENU_TYPE_NAME } from "./menuTypeName";
    import { convertJavascriptTicksToDotnetTicks } from "Util/clientDate";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/navHeader.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { responsiveManager, isAtLeast } from "Responsive/manager";

    export default {
        components: {
            notifications,
            programLogo,
            productLogo,
            programsNameBtn,
            headerIcon
        },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            },
            getSupportCenterDynamicUrl: { default: null }
        },
        props: {
            hideMenuItems: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                menuTypeName: MENU_TYPE_NAME,
                collapseLevel: 0,
                showFullSearch: false,
                dropdownMenuWrapperClass: "dropdown-menu dropdown-menu-right border-0",
                pageLoadTimeTicks: convertJavascriptTicksToDotnetTicks(Date.now()),
                loggedInUserEmailTruncationLength: 18,
                DISPLAY_THRESHOLD: {
                    search: 1, // Not currently in use
                    MINIMUM_COLLAPSE: 2,
                    programSupport: 2,
                    supportCenter: 2,
                    notifications: 3,
                    policies: 3,
                    account: 4,
                    inquiry: 5,
                    programs: 8,
                    MAXIMUM: 8
                }
            };
        },
        computed: {
            ...mapI18n({ ...KEYS }, NAMESPACE),
            navigationModel() {
                return this.$app.Navigation;
            },
            showSidebarMenu() {
                return !isAtLeast(this.viewport, BP.sm);
            },
            showSearch() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.search);
            },
            showPrograms() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.programs);
            },
            showInquiryInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.inquiry) && this.hasInquiryMenu;
            },
            showInquiryInMenu() {
                return !this.showInquiryInHeader && this.hasInquiryMenu;
            },
            showProgramSupportInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.programSupport) && this.hasProgramSupportMenu;
            },
            showProgramSupportInMenu() {
                return !this.showProgramSupportInHeader && this.hasProgramSupportMenu;
            },
            showSupportInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.supportCenter) && this.supportCenterUrl;
            },
            showSupportInMenu() {
                return !this.showSupportInHeader && this.supportCenterUrl;
            },
            showNotificationsInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.notifications) && this.hasNotificationsMenu;
            },
            showNotificationsInMenu() {
                return !this.showNotificationsInHeader && this.hasNotificationsMenu;
            },
            showAccountInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.account);
            },
            showMoreMenu() {
                return this.collapseLevel >= this.DISPLAY_THRESHOLD.MINIMUM_COLLAPSE;
            },
            showPoliciesInHeader() {
                return this.shouldShow(this.DISPLAY_THRESHOLD.policies) && this.hideMenuItems;
            },
            showPoliciesInMenu() {
                return !this.showPoliciesInHeader && this.hideMenuItems;
            },
            programsCollapseLevel() {
                // This is passed to the programsNameBtn component that displays the program
                //  selector when the button displaying the current program is clicked.
                return Math.max(this.collapseLevel - 5, 0);
            },
            programLogoUrl() {
                return `${this.$app.AgencyLogoUrl}/${this.$app.ProgramInfo.AgencyImgId}`;
            },
            supportCenterUrl() {
                const scMenu = this.navigationModel.find(
                    (navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.DesignSupport
                );
                if (!scMenu) {
                    return null;
                }

                if (this.getSupportCenterDynamicUrl != null) {
                    return this.getSupportCenterDynamicUrl();
                }

                return scMenu.Url;
            },
            inquiryMenu() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.Inquiry);
            },
            hasInquiryMenu() {
                return Boolean(this.inquiryMenu);
            },
            inquiryMenuLabel() {
                return this.hasInquiryMenu && this.inquiryMenu.Text
                    ? this.inquiryMenu.Text
                    : this.defaultInquiryMenuLabel;
            },
            programSupportMenu() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName == MENU_TYPE_NAME.ProgramSupport);
            },
            hasProgramSupportMenu() {
                return Boolean(this.programSupportMenu);
            },
            programSupportLabel() {
                return this.programSupportMenu.Text;
            },
            notificationsMenu() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.Notifications);
            },
            hasNotificationsMenu() {
                return Boolean(this.notificationsMenu);
            },
            notificationsDropdownStyle() {
                if (responsiveManager.lg.value) {
                    return {
                        width: "445px"
                    };
                } else if (responsiveManager.sm.value) {
                    return {
                        width: "305px"
                    };
                }
                return {
                    width: "100%"
                };
            },
            notificationsUrl() {
                return this.hasNotificationsMenu ? this.notificationsMenu.Url : "";
            },
            manageAccountMenuItem() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.ManageAccount);
            },
            privacyPolicyMenuItem() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.PrivacyPolicy);
            },
            termsOfUseMenuItem() {
                return this.navigationModel.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.TermsOfUse);
            },
            loggedInUserEmailTooltipText() {
                const email = this.$app.LoggedInUser.Email;
                if (email.length > this.loggedInUserEmailTruncationLength) {
                    return email;
                }

                // Using the property of the Bootstrap CSS tooltip that if the 'title' attribute is empty, no tooltip
                // is shown. If the email is not truncated, then don't show a tooltip.
                return "";
            },
            loggedInUserEmail() {
                const email = this.$app.LoggedInUser.Email;
                if (email.length > this.loggedInUserEmailTruncationLength) {
                    return email.substring(0, this.loggedInUserEmailTruncationLength - 1) + "...";
                }

                return email;
            }
        },
        mounted() {
            if (typeof ResizeObserver === "function") {
                this.resizeObserver = new ResizeObserver(() => {
                    window.requestAnimationFrame(this.calcHeader);
                });
                this.resizeObserver.observe(this.$refs.wrapper);
            } else {
                if (this.viewport) {
                    this.viewport.addUpdatedListener(this.calcHeader);
                }
                responsiveManager.addResizeListener(this.calcHeader);
            }
            this.calcHeader();
        },
        beforeUnmount() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },
        methods: {
            shouldShow(threshold) {
                return this.collapseLevel < threshold;
            },
            async calcHeader() {
                this.collapseLevel = 0;
                // hide full search when page size changes.
                // prevents situations where it can flicker in and out and when we show this one and the inline search.
                this.showFullSearch = false;
                await this.$nextTick();

                while (
                    this.collapseLevel < this.DISPLAY_THRESHOLD.MAXIMUM &&
                    this.$refs.wrapper.scrollWidth > this.$refs.wrapper.clientWidth
                ) {
                    this.collapseLevel++;
                    await this.$nextTick();
                }
            },
            showSidebarLeft() {
                this.$app.showDialog({
                    customComponent: navSidebar,
                    customComponentOptions: {
                        initalExpandState: true,
                        showExpander: false,
                        showSelfDismiss: true
                    },
                    dismissOnOutsideClick: true,
                    sidePanel: true,
                    left: true,
                    fitContent: true,
                    hideBodyPadding: true
                });
            },
            showProgramsSidebar() {
                this.$app.showDialog({
                    customComponent: navPrograms,
                    customComponentOptions: {
                        isSidebar: true
                    },
                    dismissOnOutsideClick: true,
                    sidePanel: true,
                    hideBodyPadding: true
                });
            },
            showProgramSupportDialog() {
                this.$app.showDialog({
                    title: this.programSupportLabel,
                    customComponent: supportDialog,
                    small: false,
                    scrollable: false,
                    buttons: [
                        {
                            text: this.$app.res({
                                namespace: COMMON_NAMESPACE,
                                key: COMMON_KEYS.closeText
                            })
                        }
                    ]
                });
            }
        }
    };
</script>

<style lang="scss">
    @import "./_nav-style-vars.scss";
    .pc-nav-header {
        height: $pc-nav-header-height;
    }
</style>

<style lang="scss" scoped>
    .search-min-width {
        min-width: 300px;
    }
</style>
